'use client'
import React, { useState } from 'react'
import Image from 'next/image'
import { FiTag } from "react-icons/fi";
import { SlMagnifierAdd } from "react-icons/sl";
import { FaRegTrashAlt } from "react-icons/fa";
import { useChat } from '@/app/lib/context/ChatContext';
import { snapShotViewConstants } from './snapShotView';
import ZoomModal from './zoomModal';
import Modal from '@/app/components/Modal';
import { useDeleteTemplate } from '@/app/lib/hooks/useDeleteTemplate';
import copyLogo from '@/app/assets/icons/copy_logo.svg';
import editLogo from '@/app/assets/icons/edit_logo.svg';
import filledEditLogo from '@/app/assets/icons/edit_logo_filled.svg';

export const templateType = {
    snapShotImage: 'snapShotImage',
    photoRealisticImage: 'photoRealisticImage',
    lifeStyleImage: 'lifeStyleImage',
    supplierLifeStyleImage: 'supplierLifeStyleImage'
}
interface TemplateCardProps {
    templateId: string;
    tags: Array<any>;
    description: Array<any>;
    templateImage: string;
    hiresImageUrl?: string | undefined;
    showGenerateButton: boolean;
    showTagIcon: boolean;
    handleOpenParticularSnapShotView: (id: string) => void;
    handleCurrentView: (currentView: string) => void;
    templateType: string;
    showDeleteButton: boolean;
    showReplaceButton?: boolean;
    handleRefetchReplacedProducts?: () => void;
    showEditButton?: boolean;
    onClickEditButton?: (id: any) => void;
    imageFit?: 'cover' | 'contain';
    setSelectedDesignLifeStyleImageIdForEdit?: (id: string) => void;
    showMagnifier?: boolean;
    showCloneIcon?: boolean;
    handleOnClickClone?: () => void;
}
const TemplateCard: React.FC<TemplateCardProps> = (props) => {
    const { templateId,
        tags, description,
        templateImage, showGenerateButton,
        showTagIcon, handleOpenParticularSnapShotView,
        handleCurrentView, templateType,
        showDeleteButton, showReplaceButton = false,
        handleRefetchReplacedProducts = () => { }, showEditButton = false,
        onClickEditButton = () => { }, imageFit = 'contain',
        setSelectedDesignLifeStyleImageIdForEdit = () => { }, showMagnifier = true, showCloneIcon = false, handleOnClickClone, hiresImageUrl } = props
    const { handleSetOpenChat, handleSetIsEditMode, handleSelectedLifeStyleImageId, selectedLifeStyleImageId } = useChat()
    const { deleteTemplate } = useDeleteTemplate()
    const [isActive, setIsActive] = useState<boolean>(false);
    const [showTags, setShowTags] = useState(false);
    const [showZoomModal, setShowZoomModal] = useState(false);
    const [modalOpen, setModalOpen] = useState(false)
    return <>
        <div
            className="relative group cursor-pointer w-full h-full"
            onMouseEnter={() => setIsActive(true)}
            onMouseLeave={() => { setIsActive(false); setShowTags(false) }}
        >
            <Image
                onClick={() => { handleOpenParticularSnapShotView(templateId) }}
                className={`${"object-" + imageFit} w-full h-full`}
                height={500}
                width={500}
                src={templateImage}
                alt={templateId}
                priority
            />

            {/* Hover Button */}
            {showGenerateButton && (
                <button onClick={() => { handleSetOpenChat({ isOpen: true, from: templateType }); handleSetIsEditMode(false); handleSelectedLifeStyleImageId(templateId) }}
                    className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-transparent text-white font-bold border-3 border-white px-8 py-2 rounded-3xl transition cursor-pointer">
                    Generate Additional Scenes
                </button>
            )}
            {
                showReplaceButton && (
                    <button
                        className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-transparent text-white font-bold  border-3 border-white px-8 py-2 rounded-3xl transition cursor-pointer"
                        onClick={() => handleRefetchReplacedProducts()}
                    >
                        Personalize with my product
                    </button>
                )
            }
            {
                showEditButton && (
                    <button
                        className='absolute top-2 right-2 text-white p-1 rounded cursor-pointer'
                        onClick={() => { onClickEditButton(templateId) }}
                    >
                        {
                            selectedLifeStyleImageId === templateId ?
                                <Image src={filledEditLogo} alt="Copy" width={26} height={26} />
                                : <Image src={editLogo} alt="Edit" width={26} height={26} />
                        }
                    </button>
                )
            }

            {
                showCloneIcon && (!showTagIcon) && (
                    <button className="text-white absolute top-2 left-2 p-1 rounded cursor-pointer"
                        onClick={() => { handleOnClickClone && handleOnClickClone() }}>
                       <Image src={copyLogo} alt="Copy" width={26} height={26} />
                    </button>
                )
            }
            {/* Tag Icon */}
            {isActive && showTagIcon && (
                <button
                    className="text-white absolute top-2 left-2 p-1 rounded cursor-pointer"
                    onClick={() => setShowTags((prev) => !prev)}
                >
                    <FiTag size={24} />
                </button>
            )}
            {isActive && showMagnifier && (
                <button
                    className="absolute bottom-2 left-2 text-white p-1 rounded cursor-pointer"
                    onClick={() => setShowZoomModal(true)}
                >
                    <SlMagnifierAdd size={24} />
                </button>
            )}
            {(isActive && showDeleteButton) && (
                <button
                    className="absolute bottom-2 right-2 text-white p-1 rounded cursor-pointer"
                    onClick={() => { setModalOpen(true) }}
                >
                    <FaRegTrashAlt size={24} />
                </button>
            )}

            {/* Tag Container */}
            {showTags && isActive && (
                <div className="absolute top-10 left-2 bg-white p-4 shadow-lg rounded-lg w-[320px] max-h-[260px] z-10 border border-gray-200">
                    <div className="flex flex-wrap gap-2 mb-3">
                        {tags?.slice(0, 4)?.map((tag, idx) => (
                            <span
                                key={idx}
                                className="bg-gray-400 text-white text-sm px-3 py-1 rounded-full"
                            >
                                {tag || ''}
                            </span>
                        ))}
                        {/* <span onClick={() => { handleCurrentView(snapShotViewConstants.editTagsMode) }} className="text-teal-600 text-sm font-medium cursor-pointer">Add more</span> */}
                    </div>

                    {description && <div className="text-sm text-gray-800">
                        <h3 className="font-semibold mb-2 text-gray-900">Description</h3>
                        <p className='text-gray-600 line-clamp-3'>{description || ''}</p>
                        <span onClick={() => { setSelectedDesignLifeStyleImageIdForEdit(templateId); handleCurrentView(snapShotViewConstants.editTagsMode) }} className="text-teal-600 text-sm font-medium cursor-pointer mt-2 inline-block">View more</span>
                    </div>}
                </div>
            )}
            {showZoomModal && (
                <ZoomModal templateImage={templateImage} setShowZoomModal={setShowZoomModal} hiresImageUrl={hiresImageUrl} />
            )}
            <Modal modalOpen={modalOpen} setModalOpen={setModalOpen} closeOnOutsideClick={false}>
                <div className="w-[430px] h-[200px] p-2 text-center bg-white rounded-lg shadow-lg flex flex-col justify-center">
                    <h1 className="text-xl font-semibold mb-2"> Delete Confirmation</h1>
                    <h3 className="text-sm text-gray-600">Do you want to delete your image?</h3>
                    <div className="mt-6 flex justify-center gap-4">
                        <button
                            className="text-lg font-semibold text-[#FE6526] bg-white rounded-full px-8 py-2 border-4 border-[#FE6526] transition-transform duration-200 hover:scale-105 cursor-pointer"
                            onClick={() => {
                                deleteTemplate({ templateId: templateId, type: templateType })
                                setModalOpen(false);
                            }}
                        >
                            Yes
                        </button>
                        <button
                            className="text-lg font-semibold text-white bg-[#FE6526] rounded-full px-8 py-2 border-4 border-[#FE6526] transition-transform duration-200 hover:scale-105 cursor-pointer"
                            onClick={() => {
                                setModalOpen(false)
                            }}
                        >
                            No
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    </>
}
export default TemplateCard